import type {
  DynamicListWidgetConfig,
  StaticListWidgetConfig,
  ShortcutWidgetConfig,
  WidgetType,
  WidgetConfig,
} from "./WidgetConfig";
import { logger } from "@/lib/logger";

// Re-export for convenience
export type {
  WidgetType,
  WidgetConfig,
  DynamicListWidgetConfig,
  StaticListWidgetConfig,
  ShortcutWidgetConfig,
};

// ===== Dashboard API Response Types =====

export interface DashboardResponse {
  id: string;
  tenantId: string;
  userId: string | null;
  name: string;
  isDefault: boolean;
  configJson: string;
  isOrganizationDashboard: boolean;
  isPublished: boolean;
  hasUnpublishedChanges: boolean;
  isSharedWithSubTenants: boolean;
  isFromParentTenant: boolean;
  parentTenantName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WidgetTemplateResponse {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  configJson: string;
  createdAt: string;
  updatedAt: string;
}

// ===== Dashboard Request Types =====

export interface CreateDashboardRequest {
  name: string;
  configJson?: string;
  isOrganizationDashboard?: boolean;
}

export interface UpdateDashboardRequest {
  name: string;
  configJson: string;
}

export interface PublishDashboardRequest {
  isPublished: boolean;
}

export interface ShareDashboardRequest {
  isShared: boolean;
}

export interface CloneDashboardAsNewRequest {
  name: string;
  isOrganizationDashboard?: boolean;
}

export interface CreateWidgetTemplateRequest {
  name: string;
  description?: string;
  configJson?: string;
}

export interface UpdateWidgetTemplateRequest {
  name: string;
  description?: string;
  configJson: string;
}

// ===== Parsed Dashboard Types (Frontend use) =====

export interface Dashboard {
  id: string;
  tenantId: string;
  userId: string | null;
  name: string;
  isDefault: boolean;
  isOrganizationDashboard: boolean;
  /** Organization dashboards are drafts until published; only then can anyone else see them (LAR-93). */
  isPublished: boolean;
  /** A published dashboard that has been edited since — viewers still see the published version. */
  hasUnpublishedChanges: boolean;
  /** A published organization dashboard reaches sub-tenants only once it is also released (LAR-93). */
  isSharedWithSubTenants: boolean;
  isFromParentTenant: boolean;
  parentTenantName: string | null;
  config: DashboardConfig;
  createdAt: Date;
  updatedAt: Date;
}

export interface WidgetTemplate {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  config: WidgetTemplateConfig;
  createdAt: Date;
  updatedAt: Date;
}

// ===== Dashboard Configuration (stored in ConfigJson) =====

export interface DashboardConfig {
  widgets: WidgetInstance[];
}

export interface WidgetInstance {
  id: string;
  templateId?: string;
  type: WidgetType;
  name: string;
  position: WidgetPosition;
  config: WidgetConfig;
}

export interface WidgetPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

// ===== Widget Template Configuration =====

export interface WidgetTemplateConfig {
  type: WidgetType;
  defaultName: string;
  defaultSize: { w: number; h: number };
  config: WidgetConfig;
}

// ===== Helper Functions =====

export const parseDashboardResponse = (
  response: DashboardResponse
): Dashboard => {
  let config: DashboardConfig = { widgets: [] };
  try {
    config = JSON.parse(response.configJson) as DashboardConfig;
  } catch {
    logger.warn("Failed to parse dashboard config, using default", {
      operation: "parseDashboardResponse",
      dashboardId: response.id,
    });
  }

  return {
    id: response.id,
    tenantId: response.tenantId,
    userId: response.userId,
    name: response.name,
    isDefault: response.isDefault,
    isOrganizationDashboard: response.isOrganizationDashboard,
    isPublished: response.isPublished,
    hasUnpublishedChanges: response.hasUnpublishedChanges,
    isSharedWithSubTenants: response.isSharedWithSubTenants,
    isFromParentTenant: response.isFromParentTenant,
    parentTenantName: response.parentTenantName,
    config,
    createdAt: new Date(response.createdAt),
    updatedAt: new Date(response.updatedAt),
  };
};

export const parseWidgetTemplateResponse = (
  response: WidgetTemplateResponse
): WidgetTemplate => {
  let config: WidgetTemplateConfig = {
    type: "dynamic-list",
    defaultName: "New Widget",
    defaultSize: { w: 6, h: 4 },
    config: {
      dataSource: "sources",
      filters: {},
      sortBy: "CreatedAt",
      sortAscending: false,
      visibleColumns: [],
      pageSize: 10,
    },
  };
  try {
    config = JSON.parse(response.configJson) as WidgetTemplateConfig;
  } catch {
    logger.warn("Failed to parse widget template config, using default", {
      operation: "parseWidgetTemplateResponse",
      templateId: response.id,
    });
  }

  return {
    id: response.id,
    tenantId: response.tenantId,
    name: response.name,
    description: response.description,
    config,
    createdAt: new Date(response.createdAt),
    updatedAt: new Date(response.updatedAt),
  };
};

export const serializeDashboardConfig = (config: DashboardConfig): string => {
  return JSON.stringify(config);
};

export const serializeWidgetTemplateConfig = (
  config: WidgetTemplateConfig
): string => {
  return JSON.stringify(config);
};
